import React, { ReactNode, useState } from "react"
import { XMarkIcon } from "@heroicons/react/24/outline"
import { DirectUpload } from "@rails/activestorage"

import Modal from "../../../components/modal"
import Input from "../../../components/input"
import { useNana } from "../../../hooks/useNana"
import * as Commands from "../../../../model/commands"
import { Idea, WebSource } from "../../../../model/types"
import GoalAutocomplete from "../../../components/goal_autocomplete"
import IdeaBox from "../../../components/idea_box"
import { AutocompleteResult } from "../../../components/autocomplete"
import { generatePublicId } from "../../../../model/public_id"
import api from "../../../../services/api"

const validateURL = (value: string) => {
  try {
    const url = new URL(value)
    if (!url.protocol) return "is missing a protocol (e.g. https://)"
    if (!url.host) return "is missing a host"
    if (!url.host.includes(".")) return "invalid host"
  } catch {
    return "Invalid URL"
  }
  return null
}

const validateOptionalURL = (value: string) => {
  if (value.trim() === "") return null
  return validateURL(value)
}

const validatePDFFile = (file: File | null) => {
  if (!file) return "Choose a PDF file"
  if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) return null
  return "Choose a PDF file"
}

const uploadPDF = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const upload = new DirectUpload(file, "/api/sources/pdf_direct_uploads")
    upload.create((error, blob) => {
      if (error) {
        reject(error)
        return
      }

      resolve(blob.signed_id)
    })
  })
}

type SourceFormMode = "web" | "pdf"

export default function NewSourceForm({ children }: { children: (open: () => void) => ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [mode, setMode] = useState<SourceFormMode>("web")
  const [value, setValue] = useState("")
  const [pdfUrlValue, setPdfUrlValue] = useState("")
  const [pdfFile, setPdfFile] = useState<File | null>(null)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [alreadyExists, setAlreadyExists] = useState(false)
  const [selectedGoal, setSelectedGoal] = useState<Idea | null>(null)

  const { model, commander, sync } = useNana()

  const open = () => {
    setIsOpen(true)
    setMode("web")
    setValue("")
    setPdfUrlValue("")
    setPdfFile(null)
    setHasSubmitted(false)
    setIsSubmitting(false)
    setSubmitError(null)
    setAlreadyExists(false)
    setSelectedGoal(null)
  }

  const newSourceFromUrl = (url_s: string): WebSource => {
    return {
      id: generatePublicId(),
      url: url_s,
      status: "new",
      createdAt: new Date(),
      updatedAt: new Date(),
      goalId: selectedGoal?.id,
    }
  }

  const newSourceFromPDF = (url: string): WebSource => {
    return {
      id: generatePublicId(),
      url: url || null,
      name: "Uploaded PDF",
      type: "pdf",
      status: "new",
      createdAt: new Date(),
      updatedAt: new Date(),
      goalId: selectedGoal?.id,
      hasStoredContent: true,
      contentFileContentType: "application/pdf",
    }
  }

  const createSourceIfNotExists = (url_s: string): boolean => {
    const url = new URL(url_s)
    if (model.getSourceByURL(url)) return false
    const newSource = newSourceFromUrl(url_s)
    commander.exec(
      new Commands.CreateWebSourceCommand({
        id: newSource.id,
        source: newSource,
        relatedIdeaIds: [],
        fleetingIdeas: [],
      }),
    )
    return true
  }

  const submitWebSource = () => {
    if (validateURL(value)) return
    if (createSourceIfNotExists(value.trim())) {
      setIsOpen(false)
    } else {
      setAlreadyExists(true)
    }
  }

  const submitPDFSource = async () => {
    const fileError = validatePDFFile(pdfFile)
    const urlError = validateOptionalURL(pdfUrlValue)
    if (fileError || urlError || !pdfFile) return

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const signedBlobId = await uploadPDF(pdfFile)
      const newSource = newSourceFromPDF(pdfUrlValue.trim())
      commander.exec(
        new Commands.CreateWebSourceCommand({
          id: newSource.id,
          source: newSource,
          relatedIdeaIds: [],
          fleetingIdeas: [],
        }),
      )
      await sync.immediateSync()
      await api().post(`/api/sources/${newSource.id}/pdf`, {
        signed_blob_id: signedBlobId,
      })
      setIsOpen(false)
    } catch (error) {
      console.error("Could not upload PDF source", error)
      setSubmitError("Could not upload PDF source. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const submit = () => {
    setHasSubmitted(true)
    if (isSubmitting) return
    if (mode === "web") {
      submitWebSource()
    } else {
      void submitPDFSource()
    }
  }

  return (
    <div
      data-testid="new-source-button-and-form"
      className="normal-case text-base flex items-center justify-center"
    >
      {children(open)}
      <Modal
        open={isOpen}
        primaryButton={
          alreadyExists
            ? { text: "Close", action: () => setIsOpen(false), disabled: false }
            : {
                text: isSubmitting ? "Uploading..." : "Add source",
                action: () => submit(),
                disabled: isSubmitting,
              }
        }
        secondaryButton={
          alreadyExists
            ? undefined
            : { text: "Cancel", action: () => setIsOpen(false), disabled: false }
        }
        leftButton={{
          text: "",
          action: null,
          disabled: true,
          //   text: inMultiView ? "Add one source" : "Import many sources",
          //   action: () => setInMultiView(!inMultiView),
          //   disabled: false,
        }}
        close={() => setIsOpen(false)}
      >
        {alreadyExists ? (
          <h1 className="text-lg font-medium border-b border-gray-200 dark:border-gray-600">
            You already have this source
          </h1>
        ) : (
          <>
            <h1 className="text-lg font-medium border-b border-gray-200 dark:border-gray-600">
              Add source
            </h1>
            <form
              className="mt-4"
              onSubmit={(e) => {
                e.preventDefault()
                submit()
              }}
            >
              <div className="mb-4 flex overflow-hidden rounded-md border border-gray-200 text-sm dark:border-gray-600">
                <button
                  type="button"
                  onClick={() => setMode("web")}
                  className={`flex-1 px-3 py-2 ${
                    mode === "web"
                      ? "bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-200"
                      : "bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                  }`}
                >
                  Web link
                </button>
                <button
                  type="button"
                  onClick={() => setMode("pdf")}
                  className={`flex-1 border-l border-gray-200 px-3 py-2 dark:border-gray-600 ${
                    mode === "pdf"
                      ? "bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-200"
                      : "bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                  }`}
                >
                  Uploaded PDF
                </button>
              </div>
              {mode === "web" ? (
                <Input
                  label="Web source URL"
                  name="url"
                  type="text"
                  placeholder="URL"
                  error={hasSubmitted ? validateURL(value) : null}
                  disabled={isSubmitting}
                  value={value}
                  onChange={(newValue) => setValue(newValue)}
                  testid="new-source-url-input"
                />
              ) : (
                <div>
                  <label className="block mb-2 text-sm font-medium leading-6 text-gray-900 dark:text-gray-50">
                    PDF file
                  </label>
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(event) => setPdfFile(event.target.files?.[0] || null)}
                    disabled={isSubmitting}
                    data-testid="new-source-pdf-input"
                    className="block w-full rounded-md border border-gray-300 bg-white p-2 text-sm text-gray-900 file:mr-3 file:rounded file:border-0 file:bg-sky-50 file:px-3 file:py-1 file:text-sm file:text-sky-700 hover:file:bg-sky-100 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:file:bg-sky-950 dark:file:text-sky-200"
                  />
                  {hasSubmitted && validatePDFFile(pdfFile) ? (
                    <div className="mt-1 text-sm text-red-600">{validatePDFFile(pdfFile)}</div>
                  ) : null}
                  <div className="mt-4">
                    <Input
                      label="Original URL (optional)"
                      name="pdf-url"
                      type="text"
                      placeholder="URL"
                      error={hasSubmitted ? validateOptionalURL(pdfUrlValue) : null}
                      disabled={isSubmitting}
                      value={pdfUrlValue}
                      onChange={(newValue) => setPdfUrlValue(newValue)}
                      testid="new-source-pdf-url-input"
                    />
                  </div>
                  {submitError ? (
                    <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-100">
                      {submitError}
                    </div>
                  ) : null}
                </div>
              )}
              <label className="block mt-4 mb-2 text-sm font-medium leading-6 text-gray-900 dark:text-gray-50">
                Associated goal (optional)
              </label>
              {selectedGoal ? (
                <div className="flex justify-between items-center text-sm text-gray-700 dark:text-gray-100">
                  <div className="flex justify-start items-center gap-3">
                    <IdeaBox idea={selectedGoal} />
                    <div>{selectedGoal.title}</div>
                  </div>
                  <div onClick={() => setSelectedGoal(null)} className="cursor-pointer">
                    <XMarkIcon className="h-5 w-5 text-gray-500 hover:text-gray-300" />
                  </div>
                </div>
              ) : (
                <GoalAutocomplete
                  select={(result: AutocompleteResult<Idea>, _filter) => {
                    if (result.type == "none") return true
                    const goal = result.item
                    setSelectedGoal(goal)
                    return true
                  }}
                  onEscape={() => setIsOpen(false)}
                  inputStyle="form"
                />
              )}
            </form>
          </>
        )}
      </Modal>
    </div>
  )
}
